import { useEffect } from 'react';

// Public-site canonical origin. Apex is canonical (www. is 301-redirected at
// the ingress); see project_canonical_hostname memo.
export const CANONICAL_ORIGIN = 'https://omegatrade.co.uk';

// ── SSR head sink ──────────────────────────────────────────────────────────
// During static prerender (scripts/prerender.ts) there is no `document`, so the
// effect-based head updates below never run. Instead useSeo / useJsonLd write
// the page's head data into this module-level sink *synchronously during
// render*; the prerender script reads it after renderToString and bakes it into
// the static HTML. On the client the sink is untouched and the effects drive
// the DOM exactly as before.
const IS_SERVER = typeof document === 'undefined';

export interface SsrHead {
  title?: string;
  description?: string;
  canonicalPath?: string;
  robots?: string;
  ogType?: string;
  image?: string;
  jsonld: object[];
}

export const ssrHead: SsrHead = { jsonld: [] };

export function resetSsrHead(): void {
  ssrHead.title = undefined;
  ssrHead.description = undefined;
  ssrHead.canonicalPath = undefined;
  ssrHead.robots = undefined;
  ssrHead.ogType = undefined;
  ssrHead.image = undefined;
  ssrHead.jsonld = [];
}

interface SeoOptions {
  title: string;
  description?: string;
  /** Path-only canonical, e.g. "/services". Combined with CANONICAL_ORIGIN. */
  canonicalPath?: string;
  /** Disable indexing for utility / portal pages (rarely used on marketing site). */
  noindex?: boolean;
  /** Absolute URL or root-relative path (e.g. "/assets/og-x.png") to the social
   *  share image. Overrides the site-default og:image from index.html for this
   *  page. When omitted the default (og-image.png) is left in place. */
  image?: string;
  /** og:type — 'website' (default) or 'article' for editorial/content pages. */
  ogType?: string;
}

/**
 * Per-page SEO. SPA-style: updates document.title, the description meta tag
 * and the canonical link on mount and on prop change. Robots meta is also
 * managed so portal / utility pages can opt out of indexing.
 *
 * Limitations: search engines that don't execute JS (still some niche
 * crawlers + social-media previewers) see only the static title from
 * index.html. Migrate to SSR/SSG when time permits.
 */
export function useSeo({ title, description, canonicalPath, noindex, image, ogType }: SeoOptions): void {
  // Server prerender: collect synchronously (effects don't run under SSR).
  if (IS_SERVER) {
    ssrHead.title = title;
    ssrHead.description = description;
    ssrHead.canonicalPath = canonicalPath;
    ssrHead.robots = noindex ? 'noindex, nofollow' : 'index, follow';
    ssrHead.ogType = ogType ?? 'website';
    if (image !== undefined) ssrHead.image = image;
  }
  useEffect(() => {
    document.title = title;
    if (description !== undefined) setMeta('description', description);
    setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    if (canonicalPath !== undefined) setLink('canonical', `${CANONICAL_ORIGIN}${canonicalPath}`);

    // Open Graph + Twitter mirror the title/description so social previews
    // pick up the correct page.
    setMetaProperty('og:type', ogType ?? 'website');
    setMetaProperty('og:title', title);
    if (description !== undefined) setMetaProperty('og:description', description);
    if (canonicalPath !== undefined) setMetaProperty('og:url', `${CANONICAL_ORIGIN}${canonicalPath}`);
    setMeta('twitter:title', title);
    if (description !== undefined) setMeta('twitter:description', description);

    // Per-page share image (optional). When omitted the site-default og:image
    // from index.html is left untouched so previews never break.
    if (image !== undefined) {
      const abs = image.startsWith('http') ? image : `${CANONICAL_ORIGIN}${image}`;
      setMetaProperty('og:image', abs);
      setMeta('twitter:image', abs);
    }
  }, [title, description, canonicalPath, noindex, image, ogType]);
}

/**
 * Inject a JSON-LD <script> into <head> for the current page. Pass a schema.org
 * node (or array of nodes); pass null to inject nothing. The script is tagged
 * data-dynamic and removed on change / unmount, so SPA navigation never leaves
 * a stale graph behind.
 *
 * Caveat (same as useSeo): only crawlers that execute JS see this. Googlebot
 * does; most AI-training crawlers do not — the static Organization + WebSite
 * graph in index.html and the curated llms.txt cover the no-JS case.
 */
export function useJsonLd(data: object | object[] | null): void {
  const json = data ? JSON.stringify(data) : '';
  // Server prerender: accumulate into the head sink (see note above).
  if (IS_SERVER && data) {
    ssrHead.jsonld.push(...(Array.isArray(data) ? data : [data]));
  }
  useEffect(() => {
    if (!json) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-dynamic', '');
    el.textContent = json;
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [json]);
}

function setMeta(name: string, content: string): void {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaProperty(property: string, content: string): void {
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
